import React, {ChangeEvent, FC, useEffect, useState} from 'react';

import {ShadowCard} from '@optimaxdev/design-system';
import {ActionCreatorWithPayload} from '@reduxjs/toolkit';
import {connect} from 'react-redux';

import {CloudinaryImage} from 'components/cloudinaryImage/cloudinaryImage';
import {DOCTOR_DETAILS_DEFAULT} from 'constants/product';
import {ApplicationStoreType} from 'desktop/store';

import {
    DoctorDetailsType,
    FieldType,
    MANUALLY_FILLING_METHOD,
    SMART_SEARCH_FILLING_METHOD,
} from 'types/successPage';

import {setIsAutocompleteModeEnabled} from '../../store/actions/orderItems/orderItems';
import {
    getIsAutocompleteModeEnabled,
    getValidatedItems,
} from '../../store/selectors/orderItems/orderItems';
import {getCustomerFullName} from '../../store/selectors/orderLast/orderLast';
import {
    DoctorDetailsAndUploadValidatedType,
    IsAutocompleteModeType,
} from '../../types/orderItemsTypes';

import {AutoCompleteMethodConnected} from './autoCompleteMethod/autoCompleteMethod';
import {ManualDrDetailsConnected} from './manualDrDetails/manualDrDetails';

import s from './drDetailsRedesign.scss';

export type PropsType = {
    isModalOpen: boolean | null;
    name: string;
    image: string;
    doctorDetails?: DoctorDetailsType;
    orderItemId: string;
    validatedItems?: DoctorDetailsType;
    isAutocompleteModeEnabled: boolean;
    changeField: ActionCreatorWithPayload<FieldType, string>;
    changeIsAutocompleteModeEnabled: (data: IsAutocompleteModeType) => void;
    setErrors: ActionCreatorWithPayload<DoctorDetailsAndUploadValidatedType, string>;
    customerFullName: string;
};

/**
 * This component render only dr details when customer chooses fill online
 * @param {PropsType} props - props type
 * @returns {React.ReactElement} Rendered react component
 */
export const DrDetailsRedesign: FC<PropsType> = ({
    changeField,
    changeIsAutocompleteModeEnabled,
    customerFullName,
    doctorDetails = DOCTOR_DETAILS_DEFAULT,
    image,
    isAutocompleteModeEnabled,
    isModalOpen,
    name,
    orderItemId,
    setErrors,
    validatedItems,
}) => {
    const [selectedSuggestionId, setSelectedSuggestionId] = useState<string | null>(null);
    const isNonAutocompleteMode = !isAutocompleteModeEnabled;
    useEffect(() => {
        if (customerFullName) {
            changeField({
                doctorDetails: {
                    ...doctorDetails,
                    patientName: customerFullName,
                },
                orderItemId,
            } as FieldType);
        }
    }, [customerFullName]);

    /**
     * toggle autocompleteMode in store
     */
    const toggleHandler = () => {
        changeIsAutocompleteModeEnabled({
            isAutocompleteModeEnabled: isNonAutocompleteMode,
            orderItemId,
        });
        setSelectedSuggestionId(null);
        changeField({
            doctorDetails: {
                ...DOCTOR_DETAILS_DEFAULT,
                filling_method: isNonAutocompleteMode
                    ? SMART_SEARCH_FILLING_METHOD
                    : MANUALLY_FILLING_METHOD,
                patientName: doctorDetails.patientName,
            },
            orderItemId,
        } as FieldType);
    };
    /**
     * change field sets patient name in doctor details in store
     * set errors clean errors for each input by order item id b/c customer fills the doctor details
     * @param {ChangeEvent<HTMLInputElement>} target - target
     */
    const onDoctorDetailsChange = ({target}: ChangeEvent<HTMLInputElement>) => {
        changeField({
            doctorDetails: {
                ...doctorDetails,
                [target.name]: target.value,
            },
            orderItemId,
        } as FieldType);
        setErrors({
            [orderItemId]: {
                ...validatedItems,
                [target.name]: '',
            },
        } as DoctorDetailsAndUploadValidatedType);
    };

    return (
        <ShadowCard className={s.card} corner={5} saturate={2}>
            <div className={s.cardHeader}>
                <p>Enter doctor&apos;s details.</p>
            </div>
            <div aria-label="Doctor details form" className={s.cardBody}>
                <div className={s.lensesRow}>
                    <CloudinaryImage alt={name} className={s.image} src={image} />
                    <p className={s.name}>{name}</p>
                </div>
                {isAutocompleteModeEnabled ? (
                    <AutoCompleteMethodConnected
                        changeField={changeField}
                        doctorDetails={doctorDetails}
                        isModalOpen={isModalOpen}
                        onDoctorDetailsChange={onDoctorDetailsChange}
                        orderItemId={orderItemId}
                        selectedSuggestionId={selectedSuggestionId}
                        setErrors={setErrors}
                        setSelectedSuggestionId={setSelectedSuggestionId}
                        toggleAutocomplete={toggleHandler}
                    />
                ) : (
                    <ManualDrDetailsConnected
                        doctorDetails={doctorDetails}
                        onDoctorDetailsChange={onDoctorDetailsChange}
                        orderItemId={orderItemId}
                        validatedItems={validatedItems}
                    />
                )}
                <button className={s.toggler} onClick={toggleHandler} type="button">
                    {isAutocompleteModeEnabled
                        ? 'Manually add doctor or clinic name'
                        : 'Or smart search for your doctor'}
                </button>
            </div>
        </ShadowCard>
    );
};

/**
 * Map state to props
 * @param {ApplicationStoreType} store - redux store
 * @param {string} orderItemId - order item id
 * @returns {PropsType} props
 */
const mapStateToProps = (store: ApplicationStoreType, {orderItemId}: {orderItemId: string}) => ({
    customerFullName: getCustomerFullName(store),
    isAutocompleteModeEnabled: getIsAutocompleteModeEnabled(store)[orderItemId],
    validatedItems: getValidatedItems(store)[orderItemId] as DoctorDetailsType,
});
export const DrDetailsRedesignConnected = connect(mapStateToProps, {
    changeIsAutocompleteModeEnabled: setIsAutocompleteModeEnabled,
})(DrDetailsRedesign);
